// components/system/RefreshControlPanel.jsx
import React from 'react';
import { Database, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { PUBLISHER_CLASSES, NOTIFICATION_CONFIG } from '../../constants/systemConstants';
import PublisherClassSelector from './PublisherClassSelector';

const RefreshControlPanel = ({
    selectedPublisherClasses,
    onPublisherClassesChange,
    onRefresh,
    onStopRefresh,
    isRefreshRunning = false,
    smartRecommendation = null,
    stalePublisherClasses = []
}) => {
    const handleRefresh = (type) => {
        let options = {};
        let refreshLabel = '';
        
        switch (type) {
            case 'smart':
                // Smart refresh: Use system recommendation or fall back to stale classes
                if (smartRecommendation && smartRecommendation.action !== 'NO_ACTION') {
                    options = {
                        strategy: smartRecommendation.action,
                        publisherClasses: smartRecommendation.action === 'PARTIAL_REFRESH' 
                            ? selectedPublisherClasses 
                            : PUBLISHER_CLASSES.ALL
                    };
                    refreshLabel = `Smart refresh (${smartRecommendation.action.replace('_', ' ')})`;
                } else if (stalePublisherClasses.length > 0) {
                    // Fallback to stale-only if no smart recommendation
                    options = { publisherClasses: stalePublisherClasses };
                    refreshLabel = `Smart refresh (stale classes: ${stalePublisherClasses.join(', ')})`;
                } else {
                    // Last fallback to selected classes
                    options = { publisherClasses: selectedPublisherClasses };
                    refreshLabel = `Smart refresh (selected classes)`;
                }
                break;
                
            case 'selected':
                // Refresh only user-selected publisher classes
                options = { publisherClasses: selectedPublisherClasses };
                refreshLabel = `Selected classes refresh (${selectedPublisherClasses.join(', ')})`;
                break;
                
            case 'force':
                // Force full refresh of all data
                options = { force: true };
                refreshLabel = 'Force full refresh';
                break;
                
            default:
                options = { publisherClasses: selectedPublisherClasses };
                refreshLabel = 'Selected classes refresh';
        }

        onRefresh(options, refreshLabel);
    };

    const getSmartRefreshTooltip = () => {
        if (smartRecommendation && smartRecommendation.action !== 'NO_ACTION') {
            return `Smart recommendation: ${smartRecommendation.reason}`;
        }
        if (stalePublisherClasses.length > 0) {
            return `Refresh stale classes: ${stalePublisherClasses.join(', ')}`;
        }
        return 'Intelligent refresh based on system analysis';
    };

    return (
        <div className="bg-white border rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                <Database className="w-5 h-5 text-blue-500" />
                <span>Data Refresh Controls</span>
            </h3>
            
            <div className="space-y-4">
                {/* Publisher Class Selection */}
                <PublisherClassSelector
                    selectedClasses={selectedPublisherClasses}
                    onSelectionChange={onPublisherClassesChange}
                    disabled={isRefreshRunning}
                    staleClasses={stalePublisherClasses}
                />

                {/* Streamlined Refresh Buttons */}
                <div className="space-y-3">
                    {/* Show Stop button when refresh is running */}
                    {isRefreshRunning ? (
                        <button
                            onClick={onStopRefresh}
                            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                        >
                            <AlertTriangle className="w-4 h-4" />
                            <span>🛑 Stop Refresh</span>
                        </button>
                    ) : (
                        /* Three clear, distinct refresh options */
                        <div className="flex space-x-3">
                            {/* Refresh Selected Classes - Blue */}
                            <button
                                onClick={() => handleRefresh('selected')}
                                disabled={selectedPublisherClasses.length === 0}
                                className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                title={`Refresh selected classes: ${selectedPublisherClasses.join(', ')}`}
                            >
                                <RefreshCw className="w-4 h-4" />
                                <span>Refresh Selected</span>
                            </button>
                            
                            {/* Smart Refresh - Green */}
                            <button
                                onClick={() => handleRefresh('smart')}
                                className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                                title={getSmartRefreshTooltip()}
                            >
                                <CheckCircle className="w-4 h-4" />
                                <span>Smart Refresh</span>
                            </button>
                            
                            {/* Force Full Refresh - Red */}
                            <button
                                onClick={() => handleRefresh('force')}
                                className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                                title="Force refresh all publisher classes and data"
                            >
                                <AlertTriangle className="w-4 h-4" />
                                <span>Force Full Refresh</span>
                            </button>
                        </div>
                    )}
                    
                    {/* Smart Refresh Status Indicator */}
                    {smartRecommendation && smartRecommendation.action !== 'NO_ACTION' && (
                        <div className={`p-2 rounded text-xs text-center ${
                            smartRecommendation.priority === 'HIGH' ? 'bg-red-100 text-red-700' :
                            smartRecommendation.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-blue-100 text-blue-700'
                        }`}>
                            <strong>Smart Recommendation:</strong> {smartRecommendation.reason}
                            <br />
                            <span className="opacity-75">Estimated duration: {smartRecommendation.estimatedDuration}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RefreshControlPanel;